import type { OEPRoleKey } from './roles.js';
import { OEP_ROLE_NAMES } from './roles.js';
import type { ParseResult } from './timeline.js';

export interface IOEPResource { Kind: string; Title: string; Note: string; Url: string }
/**
 * The Kind label that marks a role's office-hours resource (markup.html:679 "Zoom"). Kind is a free,
 * admin-authored display label, not a union; this constant is the one place the coupling lives, so
 * seed tooling (T31) and views agree on it.
 */
export const OEP_OFFICE_HOURS_KIND = 'Zoom';
/**
 * The Url the AGU26 seed gave every resource that does not exist yet (resources.sample.ts). Presenter
 * views hide such links (client edit 2026-10-27); a link reappears once its Url points somewhere real.
 */
export const OEP_PLACEHOLDER_RESOURCE_URL = 'https://www.agu.org/';

const withoutTrailingSlash = (url: string): string => url.trim().replace(/\/$/, '');

/** The resources whose Url is not the placeholder, in their original order. */
export function PublishedResources(list: readonly IOEPResource[]): IOEPResource[] {
    const placeholder = withoutTrailingSlash(OEP_PLACEHOLDER_RESOURCE_URL);
    return list.filter((r) => withoutTrailingSlash(r.Url) !== placeholder);
}

/** Season.Resources JSON: guidance links per role (logic.js:115-137 RESOURCES). */
export type IOEPRoleResources = Partial<Record<OEPRoleKey, IOEPResource[]>>;

const ROLE_KEYS = Object.keys(OEP_ROLE_NAMES) as OEPRoleKey[];

export function ParseRoleResources(raw: string | null): ParseResult<IOEPRoleResources> {
    if (!raw || raw.trim().length === 0) return { Ok: true, Value: {} };
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { return { Ok: false, Error: 'Resources is not valid JSON' }; }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return { Ok: false, Error: 'Resources must be an object keyed by role' };
    const out: IOEPRoleResources = {};
    for (const key of ROLE_KEYS) {
        const list = (parsed as Record<string, unknown>)[key];
        if (!Array.isArray(list)) continue;
        out[key] = list.filter((i): i is IOEPResource =>
            typeof i === 'object' && i !== null && ['Kind', 'Title', 'Note', 'Url'].every((k) => typeof (i as Record<string, unknown>)[k] === 'string'));
    }
    return { Ok: true, Value: out };
}
